import axios, { AxiosError } from "axios";
import { useSetAtom } from "jotai";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  PARTNER_AUTHORIZATION,
  PUBLIC_AUTHORIZATION,
  TOKEN_URL,
} from "@/utils/apiUrls";
import { setAuthAtom, showsSessionExpiredModalAtom } from "@/utils/atoms";
import { createReferenceId } from "@/utils/initializeSentry";

interface Props {
  onSuccess?: () => void;
  onError?: (error: AxiosError) => void;
}

interface PartnerLoginCredentials {
  email: string;
  password: string;
}

export const usePartnerLogin = (options?: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<AxiosError | null>(null);
  const setAuth = useSetAtom(setAuthAtom);
  const setShowSessionExpiredModal = useSetAtom(showsSessionExpiredModalAtom);
  const navigate = useNavigate();
  const loginAsPartner = async (credentials: PartnerLoginCredentials) => {
    setIsLoading(true);
    setError(null);

    try {
      const referenceId = createReferenceId();
      const params = new URLSearchParams({
        grant_type: "pos_login",
        username: credentials.email,
        password: credentials.password,
      });

      const response = await axios({
        method: "POST",
        url: TOKEN_URL.toString(),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: PARTNER_AUTHORIZATION,
          "X-Reference-Id": referenceId,
        },
        data: params,
      });

      if (!response.data.access_token) {
        throw new Error("No access token received");
      }

      setAuth({
        accessToken: response.data.access_token,
        refreshToken: response.data.refresh_token || null,
        isAnonymous: false,
      });
      setShowSessionExpiredModal(false);

      options?.onSuccess?.();
      navigate("/partner/cockpit");
    } catch (error) {
      const axiosError = error as AxiosError;
      setError(axiosError);

      options?.onError?.(axiosError);
    } finally {
      setIsLoading(false);
    }
  };

  const loginAsPublicPartner = async () => {
    try {
      const response = await axios({
        method: "POST",
        url: `${TOKEN_URL.toString()}?grant_type=pos_login&username=reseller@ambient.digital&password=Test123!`,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: PUBLIC_AUTHORIZATION,
        },
      });
      setAuth({
        accessToken: response.data.access_token,
        refreshToken: response.data.refresh_token || null,
        isAnonymous: true,
      });
    } catch (error) {
      console.error(`Public Partner login failed:`, error);
    }
  };

  return {
    loginAsPartner,
    loginAsPublicPartner,
    isLoading,
    error,
  };
};
